import React, { useEffect, useState } from "react";

import { DatasetsApi } from "api/DatasetsApi";
import ChartInDialogButton from "components/basic/chart-in-dialog-button/ChartInDialogButton";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import { DropdownItem } from "utils/general-models";
import { isDatasetIdentifier } from "utils/utils";
import { T } from "services/lang/LanguageService";
import "./style.scss"

interface IDatasetMultiSelectProps {
  // Dataset identifier
  id?: string;
  value?: string[] | null;
  onChange: (e: string[]) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
}

function DatasetMultiSelect(props: IDatasetMultiSelectProps) {
  const [selectedDatasets, setSelectedDatasets] = useState<DropdownItem<string>[] | null>(null);
  const [filteredDatasets, setFilteredDatasets] = useState<DropdownItem<string>[]>([]);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (props.value && props.value.length > 0) {
      const validIdentifiers = props.value.filter(id => id !== "");

      if (validIdentifiers.length > 0) {
        DatasetsApi.queryDatasets(null, 0, validIdentifiers).then(r => {
          if (!r.error && r.data) {
            const apiSelections = r.data.map(
              it =>
                ({
                  value: it.identifier,
                  label: it.toString(),
                }) as DropdownItem<string>
            );

            const emptySelections = props.value
              .filter(it => it === "")
              .map(
                () =>
                  ({
                    value: "",
                    label: "Empty",
                  }) as DropdownItem<string>
              );

            const combinedSelections = [...apiSelections, ...emptySelections];
            setSelectedDatasets(combinedSelections);
          }
        });
      } else {
        const emptySelections = props.value
          .filter(it => it === "")
          .map(
            () =>
              ({
                value: "",
                label: "Empty",
              }) as DropdownItem<string>
          );
        setSelectedDatasets(emptySelections);
      }
    }
  }, [props.value]);

  const searchDatasets = (e: AutoCompleteCompleteEvent) => {
    const searchStr = e.query.trim();

    if (searchStr.length < 3) return;

    let queryParams: [string, number, string[]] = [searchStr, 100, []];
    if (isDatasetIdentifier(searchStr)) {
      queryParams = ["", 20, [searchStr]];
    }

    DatasetsApi.queryDatasets(...queryParams).then(r => {
      if (!r.error && r.data) {
        const selectedDatasetIdentifiers = selectedDatasets?.map(dataset => dataset.value) || [];
        const selections = r.data
          .filter(it => !selectedDatasetIdentifiers.includes(it.identifier as string))
          .map(it => ({ value: it.identifier, label: it.toString() }) as DropdownItem<string>);
        setFilteredDatasets(selections);
      } else {
        setError(true);
      }
    });
  };

  const onChange = (e: AutoCompleteChangeEvent) => {
    setSelectedDatasets(e.value);

    if (typeof e.value === "string") return; // Ignore string auto complete dumb input

    props.onChange(e.value?.map((item: DropdownItem<string>) => item.value) || null);
  };

  const chipTemplate = (item: DropdownItem<string>) => {
    return item.value.length > 0 ? (
      <div className="flex nowrap gap-1 align-items-baseline mr-2">
        <span className="w-16rem sm:w-full overflow-x-auto">{item.label}</span>
        <CopyTextWithButton text="" copyableText={item.value} />
        <ChartInDialogButton identifier={item.value} header={T("word.dataset")} />
      </div>
    ) : (
      <div className="flex nowrap gap-1 align-items-baseline mr-2">
        <span>{item.label}</span>
      </div>
    );
  };

  return (
    <AutoComplete
      placeholder={T("message.type-for-search-datasets")}
      multiple
      value={selectedDatasets}
      suggestions={filteredDatasets}
      onChange={onChange}
      completeMethod={searchDatasets}
      minLength={3}
      className="dataset-multi-select"
      field="label"
      style={{ width: "100%", ...props.style }}
      disabled={props.disabled}
      selectedItemTemplate={chipTemplate}
    />
  );
}

export default DatasetMultiSelect;
